import { mentionedSkillIds } from "./ComposerMentionNode";
import type {
  BottomPopupPosition,
  PopupPosition,
} from "./ComposerContextTypes";
import { composerMentionCatalog } from "./ComposerMentionNode";
import { t } from "../../i18n";
import { memo, useEffect, useRef, useState, type ClipboardEvent } from "react";
import type {
  AgentDefinition,
  AgentWorkspaceDefinition,
  Artifact,
  Model,
  Attachment,
  ActivityGenerationSettings,
  AvailableToolDefinition,
  GenerationParameters,
  GitRepositoryDefinition,
  McpServerDefinition,
  McpToolDefinition,
  RepositorySkillDefinition,
  SkillDefinition,
  ThinkingLevel,
} from "@lumen/shared";
import { type RichTextInputHandle } from "./RichTextInput";
import {
  TRIPO_ANIMATION_MODEL_ID,
  TRIPO_RIGGING_MODEL_ID,
} from "./ComposerParameterDefinitions";
import { ComposerView } from "./ComposerView";
import { useComposerDerivedState } from "./ComposerDerivedState";
import { useComposerInteractionHandlers } from "./ComposerInteractionHandlers";
import { useComposerEditorContext } from "./useComposerEditorContext";
import {
  normalizeRepositorySelections,
  readStoredJson,
} from "./ComposerStateUtils";

export type ComposerOptions = {
  toolIds: string[];
  artifactIds: string[];
  skillIds: string[];
  mcpServerIds: string[];
  mcpToolSelections: Record<string, string[]>;
  agentWorkspaceId: string | null;
  repositoryId: string | null;
  agentWorkspaceIds?: string[];
  repositoryIds?: Record<string, string[]>;
  agentId: string | null;
  thinkingLevel?: ThinkingLevel;
  generationParameters?: GenerationParameters;
};
export type ComposerVoice = { id: string; label: string };
export type ComposerActivity =
  | "ask"
  | "image"
  | "audio"
  | "video"
  | "speech"
  | "transcription"
  | "model3d"
  | "sprite"
  | "tileset";
export type ComposerActivitySettings = NonNullable<
  GenerationParameters["activitySettings"]
>;
export type ComposerSkill = SkillDefinition | RepositorySkillDefinition;

export const Composer = memo(function Composer({
  onSend,
  onStop,
  conversationId,
  onEnvironmentConfigured,
  developmentWorkspaceLoaded = true,
  developmentConfigured = true,
  disabled,
  generating = false,
  agents = [],
  agentId,
  onAgentChange,
  supportsVision,
  supportsTools,
  supportsThinking,
  thinkingLevels = ["low", "medium", "high"],
  supportedParameters = [],
  thinkingLevel,
  onThinkingLevelChange,
  initialValue = "",
  contextInsertion,
  onContextInsertionHandled,
  onDraftChange,
  onDraftAttachmentsChange,
  initialAttachments = [],
  initialOptions,
  mode = "send",
  persistSelections = true,
  editLabel,
  onCancel,
  activity = "ask",
  onActivityChange,
  availableActivities = ["ask"],
  availableVoices = [],
  activityModels = {
    image: [],
    audio: [],
    video: [],
    speech: [],
    transcription: [],
    model3d: [],
    sprite: [],
    tileset: [],
  },
  activityModelIds = {},
  enabledActivities = ["ask"],
  activitySettings = {},
  onActivityModelChange = () => undefined,
  onActivitySettingsChange = () => undefined,
  onToggleActivity = () => undefined,
}: {
  onSend: (
    content: string,
    attachments: Attachment[],
    options: ComposerOptions,
    steer?: boolean,
  ) => Promise<void>;
  onStop?: () => void;
  conversationId?: string;
  onEnvironmentConfigured?: (configured: boolean) => void;
  developmentWorkspaceLoaded?: boolean;
  developmentConfigured?: boolean;
  disabled?: boolean;
  generating?: boolean;
  agents: AgentDefinition[];
  agentId: string;
  onAgentChange: (agentId: string) => void;
  supportsVision?: boolean;
  supportsTools?: boolean;
  supportsThinking?: boolean;
  thinkingLevels?: ThinkingLevel[];
  supportedParameters?: string[];
  thinkingLevel: ThinkingLevel | "";
  onThinkingLevelChange: (level: ThinkingLevel | "") => void;
  initialValue?: string;
  contextInsertion?: { id: number; text: string };
  onContextInsertionHandled?: (id: number) => void;
  onDraftChange?: (text: string) => void;
  onDraftAttachmentsChange?: (attachments: Attachment[]) => void;
  initialAttachments?: Attachment[];
  initialOptions?: Partial<ComposerOptions>;
  mode?: "send" | "edit";
  persistSelections?: boolean;
  editLabel?: string;
  onCancel?: () => void;
  activity?: ComposerActivity;
  onActivityChange?: (activity: ComposerActivity) => void;
  availableActivities?: ComposerActivity[];
  availableVoices?: ComposerVoice[];
  activityModels?: Record<Exclude<ComposerActivity, "ask">, Model[]>;
  activityModelIds?: Partial<Record<Exclude<ComposerActivity, "ask">, string>>;
  enabledActivities?: ComposerActivity[];
  activitySettings?: ComposerActivitySettings;
  onActivityModelChange?: (
    activity: Exclude<ComposerActivity, "ask">,
    modelId: string,
  ) => void;
  onActivitySettingsChange?: (
    activity: Exclude<ComposerActivity, "ask">,
    settings: ComposerActivitySettings[Exclude<ComposerActivity, "ask">],
  ) => void;
  onToggleActivity?: (activity: Exclude<ComposerActivity, "ask">) => void;
}) {
  const [value, setValue] = useState(initialValue);
  const [caretText, setCaretText] = useState(initialValue);
  const [files, setFiles] = useState<Attachment[]>(initialAttachments);
  const [uploading, setUploading] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const activityPicker = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const richInput = useRef<RichTextInputHandle>(null);
  useComposerEditorContext({
    value,
    files,
    richInput,
    setValue,
    contextInsertion,
    onContextInsertionHandled,
    onDraftChange,
    onDraftAttachmentsChange,
  });
  const [editorHeight, setEditorHeight] = useState(96);
  const [skills, setSkills] = useState<ComposerSkill[]>([]);
  const [skillsStatus, setSkillsStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [repositoryDiagnostics, setRepositoryDiagnostics] = useState<string[]>(
    [],
  );
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [artifactsStatus, setArtifactsStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [artifactDismissed, setArtifactDismissed] = useState(false);
  const [workspaceFiles, setWorkspaceFiles] = useState<
    import("./ComposerContextTypes").WorkspaceFileReference[]
  >([]);
  const [workspaceFilesStatus, setWorkspaceFilesStatus] = useState<
    "loading" | "ready" | "error"
  >("ready");
  const [preferredSkillIds, setPreferredSkillIds] = useState<string[]>(
    () => initialOptions?.skillIds ?? [],
  );
  const [mcpServers, setMcpServers] = useState<McpServerDefinition[]>([]);
  const [agentWorkspaces, setAgentWorkspaces] = useState<
    AgentWorkspaceDefinition[]
  >([]);
  const [agentWorkspacesLoaded, setAgentWorkspacesLoaded] = useState(false);
  const initialAgentWorkspaceId = initialOptions?.agentWorkspaceId;
  const initialAgentWorkspaceIds = initialOptions?.agentWorkspaceIds;
  const initialRepositoryId = initialOptions?.repositoryId;
  const initialRepositoryIds = initialOptions?.repositoryIds;
  const legacyWorkspaceId =
    initialAgentWorkspaceId ??
    localStorage.getItem("lumen:active-agent-workspace") ??
    null;
  const [agentWorkspaceIds, setAgentWorkspaceIds] = useState<string[]>(
    () =>
      initialAgentWorkspaceIds ??
      (initialAgentWorkspaceId
        ? [initialAgentWorkspaceId]
        : readStoredJson("lumen:active-agent-workspaces", [])),
  );
  const [agentWorkspaceId, setAgentWorkspaceId] = useState<string | null>(() =>
    initialAgentWorkspaceId !== undefined
      ? initialAgentWorkspaceId
      : localStorage.getItem("lumen:active-agent-workspace") || null,
  );
  const [repositories, setRepositories] = useState<GitRepositoryDefinition[]>(
    [],
  );
  const [repositoryId, setRepositoryId] = useState<string | null>(() =>
    initialOptions?.repositoryId !== undefined
      ? initialOptions.repositoryId
      : localStorage.getItem("lumen:active-repository") || null,
  );
  const [repositoryIds, setRepositoryIds] = useState<Record<string, string[]>>(
    () =>
      normalizeRepositorySelections(initialRepositoryIds) ??
      (legacyWorkspaceId
        ? {
            [legacyWorkspaceId]: [
              initialRepositoryId ??
                localStorage.getItem("lumen:active-repository") ??
                "",
            ].filter(Boolean),
          }
        : (normalizeRepositorySelections(
            readStoredJson("lumen:active-repositories", {}),
          ) ?? {})),
  );
  const [mcpServerIds, setMcpServerIds] = useState<string[]>(
    () =>
      initialOptions?.mcpServerIds ??
      (() => {
        try {
          const saved = JSON.parse(
            localStorage.getItem("lumen:active-mcps") ?? "[]",
          );
          return Array.isArray(saved)
            ? saved.filter((id): id is string => typeof id === "string")
            : [];
        } catch {
          return [];
        }
      })(),
  );
  const [mcpToolSelections, setMcpToolSelections] = useState<
    Record<string, string[]>
  >(
    () =>
      initialOptions?.mcpToolSelections ??
      (() => {
        try {
          const saved = JSON.parse(
            localStorage.getItem("lumen:mcp-tool-selections") ?? "{}",
          );
          return saved && typeof saved === "object" && !Array.isArray(saved)
            ? saved
            : {};
        } catch {
          return {};
        }
      })(),
  );
  const [mcpTools, setMcpTools] = useState<
    Record<
      string,
      {
        status: "idle" | "loading" | "ready" | "error";
        tools: McpToolDefinition[];
        error?: string;
      }
    >
  >({});
  const [mcpDetailServerId, setMcpDetailServerId] = useState<string | null>(
    null,
  );
  const [activityDetail, setActivityDetail] = useState<Exclude<
    ComposerActivity,
    "ask"
  > | null>(null);
  const [skillIndex, setSkillIndex] = useState(0);
  const [artifactIndex, setArtifactIndex] = useState(0);
  const [tools, setTools] = useState<AvailableToolDefinition[]>([]);
  const [toolIds, setToolIds] = useState<string[]>(
    () =>
      initialOptions?.toolIds ??
      (() => {
        try {
          const saved = JSON.parse(
            localStorage.getItem("lumen:active-tools") ?? "[]",
          );
          return Array.isArray(saved)
            ? saved.filter((id): id is string => typeof id === "string")
            : [];
        } catch {
          return [];
        }
      })(),
  );
  const preferencesLoaded = useRef(false);
  const initialOptionsRef = useRef(initialOptions);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [panelSection, setPanelSection] = useState<"tools" | "environment">(
    "tools",
  );
  const [agentOpen, setAgentOpen] = useState(false);
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [generationParameters, setGenerationParameters] =
    useState<GenerationParameters>(
      () =>
        initialOptions?.generationParameters ??
        readStoredJson("lumen:composer-generation-parameters", {}),
    );
  const appliedInitialOptionsSignature = useRef<string | null>(null);
  const initialOptionsSignature = JSON.stringify(initialOptions ?? null);
  useEffect(() => {
    if (initialOptionsSignature === appliedInitialOptionsSignature.current)
      return;
    appliedInitialOptionsSignature.current = initialOptionsSignature;
    initialOptionsRef.current = initialOptions;
    if (!initialOptions) {
      const storedWorkspaceId =
        localStorage.getItem("lumen:active-agent-workspace") || null;
      setAgentWorkspaceIds(
        readStoredJson(
          "lumen:active-agent-workspaces",
          storedWorkspaceId ? [storedWorkspaceId] : [],
        ),
      );
      setAgentWorkspaceId(storedWorkspaceId);
      const storedRepositoryIds = normalizeRepositorySelections(
        readStoredJson("lumen:active-repositories", {}),
      );
      setRepositoryIds(
        storedRepositoryIds ??
          (storedWorkspaceId
            ? {
                [storedWorkspaceId]: [
                  localStorage.getItem("lumen:active-repository") ?? "",
                ].filter(Boolean),
              }
            : {}),
      );
      setRepositoryId(localStorage.getItem("lumen:active-repository") || null);
      return;
    }
    if (Array.isArray(initialOptions.toolIds))
      setToolIds(
        initialOptions.toolIds.filter(
          (id): id is string => typeof id === "string",
        ),
      );
    if (Array.isArray(initialOptions.skillIds))
      setPreferredSkillIds(
        initialOptions.skillIds.filter(
          (id): id is string => typeof id === "string",
        ),
      );
    if (Array.isArray(initialOptions.mcpServerIds))
      setMcpServerIds(
        initialOptions.mcpServerIds.filter(
          (id): id is string => typeof id === "string",
        ),
      );
    if (initialOptions.mcpToolSelections)
      setMcpToolSelections(initialOptions.mcpToolSelections);
    if (Array.isArray(initialOptions.agentWorkspaceIds))
      setAgentWorkspaceIds(
        initialOptions.agentWorkspaceIds.filter(
          (id): id is string => typeof id === "string",
        ),
      );
    else if (initialOptions.agentWorkspaceId !== undefined)
      setAgentWorkspaceIds(
        initialOptions.agentWorkspaceId
          ? [initialOptions.agentWorkspaceId]
          : [],
      );
    if (initialOptions.repositoryIds)
      setRepositoryIds(
        normalizeRepositorySelections(initialOptions.repositoryIds) ?? {},
      );
    else if (initialOptions.repositoryId !== undefined)
      setRepositoryIds(
        initialOptions.agentWorkspaceId && initialOptions.repositoryId
          ? { [initialOptions.agentWorkspaceId]: [initialOptions.repositoryId] }
          : {},
      );
    if (initialOptions.generationParameters)
      setGenerationParameters(initialOptions.generationParameters);
  }, [initialOptions, initialOptionsRef, initialOptionsSignature]);
  // Edit composers do not receive the send page's live activity props. Keep
  // the message's own per-mode settings available in that case instead of
  // replacing them with an empty object on submit.
  const persistedActivitySettings =
    initialOptions?.generationParameters?.activitySettings ?? {};
  const effectiveActivitySettings =
    Object.keys(activitySettings).length > 0
      ? activitySettings
      : persistedActivitySettings;
  const persistedActivityModelIds =
    initialOptions?.generationParameters?.activityModelIds ?? {};
  const effectiveActivityModelIds =
    Object.keys(activityModelIds).length > 0
      ? activityModelIds
      : persistedActivityModelIds;
  const selectedActivitySettingsMap = {
    ...(generationParameters.activitySettings ?? {}),
    ...effectiveActivitySettings,
  };
  const modelOptionsSettings =
    activity !== "ask"
      ? {
          ...(selectedActivitySettingsMap[activity] ?? {}),
          ...(generationParameters.activityParameters?.[activity] ?? {}),
          ...(effectiveActivityModelIds[activity]
            ? { modelId: effectiveActivityModelIds[activity] }
            : {}),
        }
      : {};
  const selectedActivityModel =
    activity !== "ask"
      ? activityModels[activity]?.find(
          (model) =>
            model.id ===
            (effectiveActivitySettings[activity]?.modelId ??
              effectiveActivityModelIds[activity]),
        )
      : undefined;
  const acceptsTripoModelFile =
    activity === "model3d" &&
    (selectedActivityModel?.remoteId === TRIPO_RIGGING_MODEL_ID ||
      selectedActivityModel?.remoteId === TRIPO_ANIMATION_MODEL_ID);
  const acceptsTripoMultiview = Boolean(
    activity === "model3d" &&
    selectedActivityModel?.providerName?.toLowerCase().includes("tripo") &&
    !acceptsTripoModelFile,
  );
  const persistedActivityParameters =
    activity !== "ask"
      ? (generationParameters.activityParameters?.[activity] ?? {})
      : {};
  const composerParameterValues =
    activity === "ask"
      ? (generationParameters as unknown as Record<string, unknown>)
      : {
          ...(effectiveActivitySettings[activity] ?? {}),
          ...persistedActivityParameters,
          ...(generationParameters.voice
            ? { voice: generationParameters.voice }
            : {}),
          ...(generationParameters.format
            ? { format: generationParameters.format }
            : {}),
          ...(generationParameters.temperature !== undefined
            ? { temperature: generationParameters.temperature }
            : {}),
          ...(generationParameters.topP !== undefined
            ? { topP: generationParameters.topP }
            : {}),
          ...(generationParameters.seed !== undefined
            ? { seed: generationParameters.seed }
            : {}),
        };
  const updateComposerParameter = (key: string, value: unknown) => {
    const cleanValue = value === "" ? undefined : value;
    if (
      activity === "ask" ||
      ((activity === "audio" || activity === "speech") &&
        ["voice", "format"].includes(key))
    ) {
      setGenerationParameters((current) => ({ ...current, [key]: cleanValue }));
      return;
    }
    setGenerationParameters((current) => ({
      ...current,
      activityParameters: {
        ...(current.activityParameters ?? {}),
        [activity]: {
          ...(current.activityParameters?.[activity] ?? {}),
          [key]: cleanValue,
        },
      },
    }));
  };
  const updateModelOptions = (settings: ActivityGenerationSettings) => {
    if (activity === "ask") return;
    const modelId =
      typeof settings.modelId === "string" ? settings.modelId : undefined;
    if (modelId) onActivityModelChange?.(activity, modelId);
    onActivitySettingsChange?.(activity, {
      modelId,
    } as ComposerActivitySettings[Exclude<ComposerActivity, "ask">]);
    setGenerationParameters((current) => ({
      ...current,
      activityParameters: {
        ...(current.activityParameters ?? {}),
        [activity]: settings,
      },
    }));
  };
  const [settingsToolId, setSettingsToolId] = useState<string | null>(null);
  const [toolSaveStatus, setToolSaveStatus] = useState<
    Record<string, "saving" | "saved" | "error">
  >({});
  const toolSaveTimers = useRef(new Map<string, number>());
  const toolButton = useRef<HTMLButtonElement>(null);
  const toolPopup = useRef<HTMLDivElement>(null);
  const [toolMenuPosition, setToolMenuPosition] =
    useState<BottomPopupPosition | null>(null);
  const paramsButton = useRef<HTMLButtonElement>(null);
  const paramsPopup = useRef<HTMLDivElement>(null);
  const [paramsMenuPosition, setParamsMenuPosition] =
    useState<BottomPopupPosition | null>(null);
  const agentPicker = useRef<HTMLDivElement>(null);
  const [skillMenuPosition, setSkillMenuPosition] =
    useState<PopupPosition | null>(null);
  const [artifactMenuPosition, setArtifactMenuPosition] =
    useState<PopupPosition | null>(null);
  const {
    selectedWorkspaceLabel,
    selectedRepositoryLabel,
    selectedAgent,
    activityLabel,
    activeToolCount,
    thinkingFill,
    advancedParameterFields,
    activeParameterCount,
    slashOpen,
    skillMatches,
    artifactOpen,
    artifactMatches,
    referenceMatches,
    selectedSkill,
  } = useComposerDerivedState({
    caretText,
    activity,
    activityOpen,
    activityPicker,
    agents,
    agentId,
    agentOpen,
    agentPicker,
    agentWorkspaceId,
    agentWorkspaceIds,
    agentWorkspaces,
    agentWorkspacesLoaded,
    artifacts,
    artifactDismissed,
    workspaceFiles,
    workspaceFilesStatus,
    conversationId,
    developmentConfigured,
    developmentWorkspaceLoaded,
    enabledActivities,
    generationParameters,
    initialOptionsRef,
    persistSelections,
    mcpServerIds,
    mcpTools,
    mcpServers,
    mcpToolSelections,
    mode,
    onEnvironmentConfigured,
    panelSection,
    paramsButton,
    paramsOpen,
    paramsPopup,
    preferencesLoaded,
    repositoryId,
    repositoryIds,
    repositories,
    richInput,
    setActivityOpen,
    setAgentOpen,
    setAgentWorkspaceId,
    setAgentWorkspaceIds,
    setAgentWorkspaces,
    setAgentWorkspacesLoaded,
    setArtifacts,
    setArtifactsStatus,
    setWorkspaceFiles,
    setWorkspaceFilesStatus,
    setMcpServerIds,
    setMcpServers,
    setParamsMenuPosition,
    setParamsOpen,
    setPreferredSkillIds,
    setRepositoryId,
    setRepositoryIds,
    setRepositories,
    setSkills,
    setSkillsStatus,
    setToolIds,
    setToolMenuPosition,
    setTools,
    setSkillMenuPosition,
    setArtifactMenuPosition,
    setToolsOpen,
    setSettingsToolId,
    setToolSaveStatus,
    setMcpTools,
    setMcpDetailServerId,
    setActivityDetail,
    skills,
    skillsStatus,
    setRepositoryDiagnostics,
    slashDismissed,
    supportedParameters,
    thinkingLevel,
    toolButton,
    toolIds,
    toolPopup,
    tools,
    toolsOpen,
    value,
  });
  const {
    chooseSkill,
    chooseArtifact,
    chooseWorkspaceFile,
    updateValue,
    applyFormat,
    upload,
    updateToolSettings,
    loadMcpTools,
    setupMcp,
  } = useComposerInteractionHandlers({
    caretText,
    acceptsTripoMultiview,
    activity,
    activityModelIds,
    activitySettings,
    agentWorkspaceId,
    agentWorkspaceIds,
    artifacts,
    artifactIndex,
    conversationId,
    editorHeight,
    enabledActivities,
    generationParameters,
    initialAttachments,
    initialOptions,
    files,
    mcpServerIds,
    mcpTools,
    mcpToolSelections,
    onActivityModelChange,
    onActivitySettingsChange,
    onActivityChange,
    onCancel,
    onSend,
    onToggleActivity,
    repositoryId,
    repositoryIds,
    repositories,
    richInput,
    setArtifactDismissed,
    setArtifactIndex,
    setArtifacts,
    setArtifactsStatus,
    setFiles,
    setGenerationParameters,
    setMcpToolSelections,
    setMcpServers,
    setRepositoryIds,
    setSkillIndex,
    setSlashDismissed,
    setValue,
    skills,
    skillIndex,
    tools,
    toolIds,
    toolSaveTimers,
    setToolSaveStatus,
    setTools,
    setMcpTools,
    setUploading,
    value,
  });
  const submit = async (steer = false) => {
    const content = richInput.current?.getMarkdown() ?? value;
    if ((!content.trim() && !files.length) || disabled || uploading) return;
    const attached = files;
    const selectedArtifactIds = [
      ...new Set([
        ...files.flatMap((file) => (file.artifactId ? [file.artifactId] : [])),
        ...[
          ...content.matchAll(
            /@artifact:([a-z0-9](?:[a-z0-9._-]{0,126}[a-z0-9_-])?)/gi,
          ),
        ].map((match) => match[1]),
      ]),
    ];
    const selectedMcps = mcpServerIds;
    const selectedTools = toolIds;
    const selectedActivityParameters =
      activity === "ask"
        ? {}
        : (generationParameters.activityParameters?.[activity] ?? {});
    const selectedActivitySettings =
      activity === "ask"
        ? undefined
        : {
            ...(selectedActivitySettingsMap[activity] ?? {}),
            ...selectedActivityParameters,
          };
    const selectedGenerationParameters: GenerationParameters = {
      ...generationParameters,
      ...(Object.keys(selectedActivitySettingsMap).length || activity !== "ask"
        ? {
            activitySettings: {
              ...selectedActivitySettingsMap,
              ...(selectedActivitySettings
                ? { [activity]: selectedActivitySettings }
                : {}),
            },
          }
        : {}),
      ...(selectedActivitySettings?.voice
        ? { voice: selectedActivitySettings.voice }
        : {}),
      ...(selectedActivitySettings?.temperature !== undefined
        ? { temperature: selectedActivitySettings.temperature }
        : {}),
      ...(selectedActivitySettings?.topP !== undefined
        ? { topP: selectedActivitySettings.topP }
        : {}),
      ...(selectedActivitySettings?.maxTokens !== undefined
        ? { maxTokens: selectedActivitySettings.maxTokens }
        : {}),
      activity,
      enabledActivities: enabledActivities.filter(
        (item): item is Exclude<ComposerActivity, "ask"> => item !== "ask",
      ),
    };
    const selectedMcpTools = Object.fromEntries(
      selectedMcps
        .filter((id) => Object.hasOwn(mcpToolSelections, id))
        .map((id) => [id, mcpToolSelections[id]]),
    );
    try {
      if (mode === "send") {
        richInput.current?.setMarkdown("");
        setFiles([]);
      }
      await onSend(
        content,
        attached,
        {
          toolIds: selectedTools,
          artifactIds: selectedArtifactIds,
          skillIds:
            activity === "ask"
              ? mentionedSkillIds(content, skills, preferredSkillIds)
              : [],
          mcpServerIds: activity === "ask" ? selectedMcps : [],
          mcpToolSelections: activity === "ask" ? selectedMcpTools : {},
          agentWorkspaceId:
            agentWorkspaceIds.length === 1 ? agentWorkspaceIds[0] : null,
          repositoryId:
            agentWorkspaceIds.length === 1 &&
            (repositoryIds[agentWorkspaceIds[0]] ?? []).length === 1
              ? repositoryIds[agentWorkspaceIds[0]][0]
              : null,
          ...(agentWorkspaceIds.length ? { agentWorkspaceIds } : {}),
          ...(Object.keys(repositoryIds).length ? { repositoryIds } : {}),
          agentId: activity === "ask" ? agentId || null : null,
          ...(supportsThinking && thinkingLevel ? { thinkingLevel } : {}),
          ...(Object.keys(selectedGenerationParameters).length
            ? { generationParameters: selectedGenerationParameters }
            : {}),
        },
        steer,
      );
    } catch {
      richInput.current?.setMarkdown(content);
      setFiles((current) => (current.length ? current : attached));
      setToolIds(selectedTools);
      setGenerationParameters(selectedGenerationParameters);
    }
  };
  const keydown = (event: globalThis.KeyboardEvent) => {
    if (event.key === "Escape") {
      if (slashOpen) setSlashDismissed(true);
      if (artifactOpen) setArtifactDismissed(true);
      richInput.current?.getElement()?.blur();
      return true;
    }
    if (
      (event.metaKey || event.ctrlKey) &&
      ["b", "i"].includes(event.key.toLowerCase())
    ) {
      event.preventDefault();
      applyFormat(event.key.toLowerCase() === "b" ? "bold" : "italic");
      return true;
    }
    if (
      artifactOpen &&
      ["ArrowDown", "ArrowUp", "Enter", "Tab"].includes(event.key)
    ) {
      event.preventDefault();
      if (referenceMatches.length) {
        if (event.key === "ArrowDown")
          setArtifactIndex(
            (current) => (current + 1) % referenceMatches.length,
          );
        else if (event.key === "ArrowUp")
          setArtifactIndex(
            (current) =>
              (current - 1 + referenceMatches.length) % referenceMatches.length,
          );
        else
          (() => {
            const selected =
              referenceMatches[
                Math.min(artifactIndex, referenceMatches.length - 1)
              ];
            if (selected.kind === "artifact") chooseArtifact(selected.item);
            else chooseWorkspaceFile(selected.item);
          })();
      }
      return true;
    }
    if (
      slashOpen &&
      ["ArrowDown", "ArrowUp", "Enter", "Tab"].includes(event.key)
    ) {
      event.preventDefault();
      if (skillMatches.length) {
        if (event.key === "ArrowDown")
          setSkillIndex((current) => (current + 1) % skillMatches.length);
        else if (event.key === "ArrowUp")
          setSkillIndex(
            (current) =>
              (current - 1 + skillMatches.length) % skillMatches.length,
          );
        else
          chooseSkill(
            skillMatches[Math.min(skillIndex, skillMatches.length - 1)],
          );
      }
      return true;
    }
    if (mode === "edit") return false;
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !disabled &&
      !event.isComposing
    ) {
      event.preventDefault();
      void submit(event.metaKey || event.ctrlKey);
      return true;
    }
    return false;
  };
  const paste = (event: ClipboardEvent<HTMLDivElement>) => {
    const images = [...event.clipboardData.files].filter((file) =>
      file.type.startsWith("image/"),
    );
    if (images.length) {
      event.preventDefault();
      void upload(images);
    }
  };
  const placeholder =
    activity === "image"
      ? t("Describe the image you want to create…")
      : activity === "audio"
        ? t("Describe the audio you want to create…")
        : activity === "video"
          ? t("Describe the video you want to create…")
          : activity === "speech"
            ? t("Enter the text you want spoken…")
            : activity === "transcription"
              ? t("Add optional transcription guidance…")
              : activity === "model3d"
                ? t("Describe the 3D asset you want to create…")
                : activity === "sprite"
                  ? t("Describe the sprite or sprite sheet you want to create…")
                  : activity === "tileset"
                    ? t(
                        "Describe the tileset or terrain atlas you want to create…",
                      )
                    : t("Ask anything… Type / for skills or @ for artifacts");
  return (
    <ComposerView
      {...{
        acceptsTripoModelFile,
        activity,
        activityDetail,
        activityLabel,
        activityModelIds,
        activityModels,
        activityOpen,
        activityPicker,
        activitySettings,
        activeParameterCount,
        activeToolCount,
        advancedParameterFields,
        agentId,
        agentOpen,
        agentPicker,
        agentWorkspaceIds,
        agentWorkspaces,
        agents,
        applyFormat,
        artifactIndex,
        artifactMatches,
        referenceMatches,
        artifactMenuPosition,
        artifactOpen,
        artifacts,
        artifactsStatus,
        mentionCatalog: composerMentionCatalog(
          skills,
          artifacts,
          workspaceFiles,
        ),
        workspaceFilesStatus,
        availableActivities,
        availableVoices,
        chooseArtifact,
        chooseWorkspaceFile,
        chooseSkill,
        composerParameterValues,
        disabled,
        editorHeight,
        enabledActivities,
        fileInput,
        files,
        generating,
        generationParameters,
        initialValue,
        keydown,
        onSelectionChange: setCaretText,
        loadMcpTools,
        mcpDetailServerId,
        mcpServerIds,
        mcpServers,
        mcpToolSelections,
        mcpTools,
        mode,
        editLabel,
        modelOptionsSettings,
        onActivityChange,
        onActivityModelChange,
        onActivitySettingsChange,
        onAgentChange,
        onCancel,
        onStop,
        onThinkingLevelChange,
        onToggleActivity,
        panelSection,
        paramsButton,
        paramsMenuPosition,
        paramsOpen,
        paramsPopup,
        paste,
        placeholder,
        repositories,
        repositoryIds,
        richInput,
        selectedActivityModel,
        selectedAgent,
        selectedSkill,
        selectedRepositoryLabel,
        selectedWorkspaceLabel,
        setActivityDetail,
        setActivityOpen,
        setAgentOpen,
        setAgentWorkspaceIds,
        setEditorHeight,
        setFiles,
        setGenerationParameters,
        setMcpDetailServerId,
        setMcpServerIds,
        setMcpToolSelections,
        setPanelSection,
        setParamsOpen,
        setParamsMenuPosition,
        setRepositoryIds,
        setSettingsToolId,
        setThinkingOpen,
        setToolIds,
        setToolsOpen,
        setToolMenuPosition,
        setSkillMenuPosition,
        skillMenuPosition,
        setupMcp,
        skillIndex,
        skillMatches,
        skillsStatus,
        repositoryDiagnostics,
        slashOpen,
        submit,
        supportsThinking,
        supportsVision,
        supportedParameters,
        thinkingFill,
        thinkingLevels,
        thinkingLevel,
        thinkingOpen,
        toolButton,
        toolIds,
        toolMenuPosition,
        toolPopup,
        tools,
        toolSaveStatus,
        toolsOpen,
        settingsToolId,
        updateComposerParameter,
        updateModelOptions,
        updateToolSettings,
        updateValue,
        upload,
        uploading,
        value,
      }}
    />
  );
});
